/**
 * Google Contacts Sync Service
 *
 * Handles bidirectional sync between Google Contacts (People API) and
 * FlowState CRM clients. Maps Google Person objects to/from Client records.
 *
 * Google People API: https://people.googleapis.com/v1
 */

import { logger } from "@/lib/logger";
import { typedSupabase } from "@/lib/typed-supabase";
import type { IService, ServiceResponse } from "@/services/core/service-registry";
import type { GoogleContactsAuthService } from "./google-contacts-auth.service";

const log = logger.createNamespace('GoogleContactsSyncService');

// Google People API field masks for contacts
const PERSON_FIELDS = [
  'names',
  'emailAddresses',
  'phoneNumbers',
  'organizations',
  'addresses',
  'urls',
  'biographies',
].join(',');

export interface GoogleContact {
  resourceName: string;
  etag?: string;
  names?: Array<{ displayName: string; givenName?: string; familyName?: string }>;
  emailAddresses?: Array<{ value: string; type?: string }>;
  phoneNumbers?: Array<{ value: string; type?: string }>;
  organizations?: Array<{ name?: string; title?: string }>;
  addresses?: Array<{ streetAddress?: string; city?: string; region?: string; country?: string; postalCode?: string }>;
  urls?: Array<{ value: string }>;
  biographies?: Array<{ value: string }>;
}

export interface ContactSyncResult {
  success: boolean;
  synced: number;
  created: number;
  updated: number;
  failed: number;
  errors: Array<{ contactId?: string; error: string }>;
  duration_ms: number;
}

export interface IGoogleContactsSyncService extends IService {
  // Contact Listing
  listContacts(userId: string, pageToken?: string): Promise<ServiceResponse<{ contacts: GoogleContact[]; nextPageToken?: string }>>;

  // Sync Operations
  syncFromGoogle(userId: string): Promise<ServiceResponse<ContactSyncResult>>;
  syncToGoogle(userId: string, clientId?: string): Promise<ServiceResponse<ContactSyncResult>>;
  syncBidirectional(userId: string): Promise<ServiceResponse<ContactSyncResult>>;

  // Single contact operations
  createContact(userId: string, contact: Omit<GoogleContact, 'resourceName' | 'etag'>): Promise<ServiceResponse<GoogleContact>>;
  updateContact(userId: string, resourceName: string, contact: Partial<GoogleContact>): Promise<ServiceResponse<GoogleContact>>;
}

export class GoogleContactsSyncService implements IGoogleContactsSyncService {
  readonly name = "google-contacts-sync";
  private readonly supabase = typedSupabase;
  private authService!: GoogleContactsAuthService;

  private readonly API_BASE_URL = 'https://people.googleapis.com/v1';

  async initialize(): Promise<void> {
    log.info('Google Contacts Sync Service initialized');
  }

  async dispose(): Promise<void> {
    log.info('Google Contacts Sync Service disposed');
  }

  setAuthService(authService: GoogleContactsAuthService): void {
    this.authService = authService;
  }

  // ============================================================================
  // Contact Listing
  // ============================================================================

  async listContacts(userId: string, pageToken?: string): Promise<ServiceResponse<{ contacts: GoogleContact[]; nextPageToken?: string }>> {
    log.debug('Fetching Google contacts', { userId });

    const tokenResult = await this.authService.getValidToken(userId);
    if (!tokenResult.success || !tokenResult.data) {
      return { success: false, error: tokenResult.error ?? 'Failed to get Google token. Please connect your Google account first.' };
    }

    try {
      const params = new URLSearchParams({
        personFields: PERSON_FIELDS,
        pageSize: '100',
        ...(pageToken ? { pageToken } : {}),
      });

      const response = await fetch(
        `${this.API_BASE_URL}/people/me/connections?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${tokenResult.data.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        log.error('Failed to fetch Google contacts', { error });
        return { success: false, error: `Failed to fetch contacts: ${error.error?.message ?? response.statusText}` };
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          contacts: data.connections ?? [],
          nextPageToken: data.nextPageToken,
        },
      };
    } catch (error) {
      log.error('Error fetching Google contacts', { error });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch Google contacts',
      };
    }
  }

  // ============================================================================
  // Sync: Google → FlowState
  // ============================================================================

  async syncFromGoogle(userId: string): Promise<ServiceResponse<ContactSyncResult>> {
    const startTime = Date.now();
    const result: ContactSyncResult = {
      success: true,
      synced: 0,
      created: 0,
      updated: 0,
      failed: 0,
      errors: [],
      duration_ms: 0,
    };

    log.info('Starting Google Contacts → FlowState sync', { userId });

    // Fetch all contacts with pagination
    const allContacts: GoogleContact[] = [];
    let pageToken: string | undefined;

    do {
      const listResult = await this.listContacts(userId, pageToken);
      if (!listResult.success || !listResult.data) {
        return { success: false, error: listResult.error ?? 'Failed to list contacts' };
      }
      allContacts.push(...listResult.data.contacts);
      pageToken = listResult.data.nextPageToken;
    } while (pageToken);

    log.debug(`Fetched ${allContacts.length} Google contacts`, { userId });

    // Get current user's org for new clients
    const { data: authUser } = await this.supabase.auth.getUser();
    const orgId = authUser?.user ? await this.getUserOrgId(authUser.user.id) : null;

    for (const contact of allContacts) {
      try {
        const primaryName = contact.names?.[0]?.displayName ?? 'Unknown Contact';
        const primaryEmail = contact.emailAddresses?.[0]?.value ?? null;
        const primaryPhone = contact.phoneNumbers?.[0]?.value ?? null;
        const company = contact.organizations?.[0]?.name ?? null;
        const website = contact.urls?.[0]?.value ?? null;
        const notes = contact.biographies?.[0]?.value ?? null;

        // Check if client already mapped via google_resource_name
        const { data: existing } = await this.supabase
          .from('clients')
          .select('id, name, email')
          .eq('google_resource_name', contact.resourceName)
          .maybeSingle();

        if (existing) {
          // Update existing client
          const { error } = await this.supabase
            .from('clients')
            .update({
              name: primaryName,
              email: primaryEmail,
              phone: primaryPhone,
              company,
              website,
              notes,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);

          if (error) {
            result.failed++;
            result.errors.push({ contactId: contact.resourceName, error: error.message });
          } else {
            result.updated++;
            result.synced++;
          }
        } else if (primaryEmail) {
          // Try to match by email to avoid duplicates
          const { data: byEmail } = await this.supabase
            .from('clients')
            .select('id')
            .eq('email', primaryEmail)
            .maybeSingle();

          if (byEmail) {
            // Link existing client to Google contact
            await (this.supabase
              .from('clients') as unknown as { update: (v: Record<string, unknown>) => { eq: (col: string, val: string) => Promise<unknown> } })
              .update({ google_resource_name: contact.resourceName })
              .eq('id', byEmail.id);
            result.updated++;
          } else {
            // Create new client
            const { error } = await (this.supabase
              .from('clients') as unknown as { insert: (v: Record<string, unknown>) => Promise<{ error: { message: string } | null }> })
              .insert({
                name: primaryName,
                email: primaryEmail,
                phone: primaryPhone,
                company,
                website,
                notes,
                google_resource_name: contact.resourceName,
                ...(orgId ? { organization_id: orgId } : {}),
                status: 'active',
                lifecycle_stage: 'lead',
              });

            if (error) {
              result.failed++;
              result.errors.push({ contactId: contact.resourceName, error: error.message });
            } else {
              result.created++;
              result.synced++;
            }
          }
        }
        // Skip contacts with no email and no existing mapping — insufficient data
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        result.failed++;
        result.errors.push({ contactId: contact.resourceName, error: msg });
      }
    }

    result.duration_ms = Date.now() - startTime;
    log.success('Google Contacts → FlowState sync completed', { ...result } as Record<string, unknown>);
    return { success: true, data: result };
  }

  // ============================================================================
  // Sync: FlowState → Google
  // ============================================================================

  async syncToGoogle(userId: string, clientId?: string): Promise<ServiceResponse<ContactSyncResult>> {
    const startTime = Date.now();
    const result: ContactSyncResult = {
      success: true,
      synced: 0,
      created: 0,
      updated: 0,
      failed: 0,
      errors: [],
      duration_ms: 0,
    };

    log.info('Starting FlowState → Google Contacts sync', { userId, clientId });

    const tokenResult = await this.authService.getValidToken(userId);
    if (!tokenResult.success || !tokenResult.data) {
      return { success: false, error: tokenResult.error ?? 'Failed to get Google token' };
    }
    const accessToken = tokenResult.data.access_token;

    // Fetch clients to sync — google_resource_name is a new column
    // not yet reflected in the generated Supabase types (migration pending).
    type ClientRow = { id: string; name: string; email?: string | null; phone?: string | null; company?: string | null; website?: string | null; notes?: string | null; google_resource_name?: string | null };
    
    // Type for Supabase query builder with dynamic methods
    type SupabaseQueryBuilder<T> = {
      select: (columns: string) => SupabaseQueryBuilder<T>;
      eq: (column: string, value: unknown) => SupabaseQueryBuilder<T>;
      not: (column: string, operator: string, value: unknown) => SupabaseQueryBuilder<T>;
    };

    const clientsQuery = (this.supabase.from('clients') as unknown as SupabaseQueryBuilder<ClientRow>)
      .select('id, name, email, phone, company, website, notes, google_resource_name');

    const filteredQuery: SupabaseQueryBuilder<ClientRow> = clientId
      ? clientsQuery.eq('id', clientId)
      : clientsQuery.not('email', 'is', null);

    const { data: clients, error: fetchError } = await filteredQuery as unknown as { data: ClientRow[] | null; error: { message: string } | null };
    if (fetchError || !clients) {
      return { success: false, error: fetchError?.message ?? 'Failed to fetch clients' };
    }

    for (const client of clients) {
      try {
        const personBody = this.clientToGooglePerson(client);

        if (client.google_resource_name) {
          // Update existing Google contact
          const updateResponse = await fetch(
            `${this.API_BASE_URL}/${client.google_resource_name}:updateContact?updatePersonFields=${PERSON_FIELDS}`,
            {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(personBody),
            }
          );

          if (!updateResponse.ok) {
            const err = await updateResponse.json();
            result.failed++;
            result.errors.push({ contactId: client.id, error: err.error?.message ?? 'Update failed' });
          } else {
            result.updated++;
            result.synced++;
          }
        } else {
          // Create new Google contact
          const createResponse = await fetch(
            `${this.API_BASE_URL}/people:createContact`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(personBody),
            }
          );

          if (!createResponse.ok) {
            const err = await createResponse.json();
            result.failed++;
            result.errors.push({ contactId: client.id, error: err.error?.message ?? 'Create failed' });
          } else {
            const created = await createResponse.json();
            // Store the Google resource name on the client
            await (this.supabase
              .from('clients') as unknown as { update: (v: Record<string, unknown>) => { eq: (col: string, val: string) => Promise<unknown> } })
              .update({ google_resource_name: created.resourceName })
              .eq('id', client.id);
            result.created++;
            result.synced++;
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        result.failed++;
        result.errors.push({ contactId: client.id, error: msg });
      }
    }

    result.duration_ms = Date.now() - startTime;
    log.success('FlowState → Google Contacts sync completed', { ...result } as Record<string, unknown>);
    return { success: true, data: result };
  }

  // ============================================================================
  // Bidirectional Sync
  // ============================================================================

  async syncBidirectional(userId: string): Promise<ServiceResponse<ContactSyncResult>> {
    log.info('Starting bidirectional Google Contacts sync', { userId });

    // Pull from Google first to get latest remote state
    const fromGoogleResult = await this.syncFromGoogle(userId);
    if (!fromGoogleResult.success) {
      return fromGoogleResult;
    }

    // Push local changes to Google
    const toGoogleResult = await this.syncToGoogle(userId);
    if (!toGoogleResult.success) {
      return toGoogleResult;
    }

    // Merge results
    const merged: ContactSyncResult = {
      success: true,
      synced: fromGoogleResult.data!.synced + toGoogleResult.data!.synced,
      created: fromGoogleResult.data!.created + toGoogleResult.data!.created,
      updated: fromGoogleResult.data!.updated + toGoogleResult.data!.updated,
      failed: fromGoogleResult.data!.failed + toGoogleResult.data!.failed,
      errors: [...fromGoogleResult.data!.errors, ...toGoogleResult.data!.errors],
      duration_ms: fromGoogleResult.data!.duration_ms + toGoogleResult.data!.duration_ms,
    };

    log.success('Bidirectional Google Contacts sync completed', { ...merged } as Record<string, unknown>);
    return { success: true, data: merged };
  }

  // ============================================================================
  // Single contact create/update
  // ============================================================================

  async createContact(userId: string, contact: Omit<GoogleContact, 'resourceName' | 'etag'>): Promise<ServiceResponse<GoogleContact>> {
    const tokenResult = await this.authService.getValidToken(userId);
    if (!tokenResult.success || !tokenResult.data) {
      return { success: false, error: tokenResult.error ?? 'Failed to get token' };
    }

    const response = await fetch(`${this.API_BASE_URL}/people:createContact`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenResult.data.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contact),
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.error?.message ?? 'Failed to create Google contact' };
    }

    return { success: true, data: await response.json() };
  }

  async updateContact(userId: string, resourceName: string, contact: Partial<GoogleContact>): Promise<ServiceResponse<GoogleContact>> {
    const tokenResult = await this.authService.getValidToken(userId);
    if (!tokenResult.success || !tokenResult.data) {
      return { success: false, error: tokenResult.error ?? 'Failed to get token' };
    }

    const response = await fetch(
      `${this.API_BASE_URL}/${resourceName}:updateContact?updatePersonFields=${PERSON_FIELDS}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${tokenResult.data.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contact),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.error?.message ?? 'Failed to update Google contact' };
    }

    return { success: true, data: await response.json() };
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  private clientToGooglePerson(client: {
    name: string;
    email?: string | null;
    phone?: string | null;
    company?: string | null;
    website?: string | null;
    notes?: string | null;
  }): Partial<GoogleContact> {
    const person: Partial<GoogleContact> = {
      names: [{ displayName: client.name }],
    };

    if (client.email) {
      person.emailAddresses = [{ value: client.email, type: 'work' }];
    }
    if (client.phone) {
      person.phoneNumbers = [{ value: client.phone, type: 'work' }];
    }
    if (client.company) {
      person.organizations = [{ name: client.company }];
    }
    if (client.website) {
      person.urls = [{ value: client.website }];
    }
    if (client.notes) {
      person.biographies = [{ value: client.notes }];
    }

    return person;
  }

  private async getUserOrgId(userId: string): Promise<string | null> {
    const { data } = await this.supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();
    return data?.organization_id ?? null;
  }
}
